import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Users, Loader2, Search, RefreshCw, Download, Check, X, Filter, Trash2, AlertTriangle, Database } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to calculate age from birthdate
const calculateAge = (birthdate) => {
  if (!birthdate) return null;
  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Day number to name mapping
const dayNames = {
  0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat",
  "0": "Sun", "1": "Mon", "2": "Tue", "3": "Wed", "4": "Thu", "5": "Fri", "6": "Sat",
  "Sunday": "Sun", "Monday": "Mon", "Tuesday": "Tue", "Wednesday": "Wed", 
  "Thursday": "Thu", "Friday": "Fri", "Saturday": "Sat"
};

export default function AcademyPlayerOverview({ user }) {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [telegramFilter, setTelegramFilter] = useState("all");
  const [ageGroupFilter, setAgeGroupFilter] = useState("all");
  const [assigning, setAssigning] = useState(null);
  
  // Delete player state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, player: null, checking: false, transactionInfo: null });
  const [lockMembershipNumber, setLockMembershipNumber] = useState(true);
  const [deleting, setDeleting] = useState(false);
  
  // Reconcile state
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [playersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/academy/admin/player-overview`, { withCredentials: true }),
        axios.get(`${API}/academy/teams`, { withCredentials: true })
      ]);
      setPlayers(playersRes.data || []);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // The other names a child answers to, so a coach searching either one finds them.
  const editAliases = async (player) => {
    const current = (player.also_known_as || []).join(", ");
    const answer = window.prompt(
      `Other names ${player.child_name || player.name} is known by, separated by commas:`,
      current
    );
    if (answer === null) return;
    try {
      const r = await axios.put(
        `${API}/academy/attendance-hub/players/${player.id}/aliases`,
        { also_known_as: answer.split(",").map((a) => a.trim()).filter(Boolean) },
        { withCredentials: true }
      );
      setPlayers((prev) => prev.map((p) =>
        p.id === player.id ? { ...p, also_known_as: r.data.also_known_as } : p));
      toast.success(r.data.also_known_as.length
        ? `Known as ${r.data.also_known_as.join(", ")}`
        : "Known-as names cleared");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save those names");
    }
  };


  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const response = await axios.post(
        `${API}/academy/admin/reconcile-players`,
        {},
        { withCredentials: true }
      );
      const data = response.data;
      if (data.created_count > 0 || data.updated_count > 0) {
        toast.success(`Reconciliation complete: ${data.created_count} players created, ${data.updated_count} updated`);
        fetchData(); // Refresh the data
      } else {
        toast.info("All player data is already synchronized");
      }
    } catch (error) {
      console.error("Error reconciling players:", error);
      toast.error("Failed to reconcile player data");
    } finally {
      setReconciling(false);
    }
  };

  const handleAssignToTeam = async (playerId, teamId) => {
    if (!teamId || teamId === "unassigned") {
      // Unassign from team
      setAssigning(playerId);
      try {
        await axios.post(
          `${API}/academy/admin/unassign-player-from-team?player_id=${playerId}`,
          {},
          { withCredentials: true }
        );
        toast.success("Player unassigned from team");
        fetchData();
      } catch (error) {
        console.error("Error unassigning player:", error);
        toast.error("Failed to unassign player");
      } finally {
        setAssigning(null);
      }
      return;
    }
    
    setAssigning(playerId);
    try {
      await axios.post(
        `${API}/academy/admin/assign-player-to-team?player_id=${playerId}&team_id=${teamId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Player assigned to team!");
      fetchData();
    } catch (error) {
      console.error("Error assigning player:", error);
      toast.error("Failed to assign player");
    } finally {
      setAssigning(null);
    }
  };

  const openDeleteDialog = async (player) => {
    setDeleteDialog({ open: true, player, checking: true, transactionInfo: null });
    setLockMembershipNumber(true);
    
    try {
      const response = await axios.get(
        `${API}/academy/admin/player/${player.id}/check-transactions`,
        { withCredentials: true }
      );
      setDeleteDialog(prev => ({ ...prev, checking: false, transactionInfo: response.data }));
    } catch (error) {
      console.error("Error checking transactions:", error);
      toast.error("Failed to check player transactions");
      setDeleteDialog({ open: false, player: null, checking: false, transactionInfo: null });
    }
  };

  const handleDeletePlayer = async () => {
    if (!deleteDialog.player) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/academy/admin/player/${deleteDialog.player.id}?lock_membership_number=${lockMembershipNumber}`,
        { withCredentials: true }
      );
      toast.success(`Player "${deleteDialog.player.child_name || deleteDialog.player.name}" deleted successfully`);
      setDeleteDialog({ open: false, player: null, checking: false, transactionInfo: null });
      fetchData();
    } catch (error) {
      console.error("Error deleting player:", error);
      toast.error(error.response?.data?.detail || "Failed to delete player");
    } finally {
      setDeleting(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["Player Name", "Membership #", "Age", "Gender", "Age Group", "Enrolled Sessions", "Parent Name", "Parent Email", "Parent Phone", "Telegram", "Team"];
    const rows = filteredPlayers.map(p => [
      p.name || p.child_name || "",
      p.membership_number || "",
      calculateAge(p.birthdate) || "",
      p.gender || "",
      p.age_group || "",
      (p.enrolled_sessions || []).map(s => `${dayNames[s.day_of_week] || s.day_of_week} ${s.time}`).join("; "),
      p.parent_name || "",
      p.parent_email || "",
      p.parent_phone || "",
      p.parent_telegram ? "Yes" : "No",
      p.team_name || "Unassigned"
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `player_overview_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("CSV exported!");
  };

  // Get unique age groups for filter
  const ageGroups = [...new Set(players.map(p => p.age_group).filter(Boolean))].sort();

  // Filter players
  const filteredPlayers = players.filter(p => {
    const name = (p.name || p.child_name || "").toLowerCase();
    const parentName = (p.parent_name || "").toLowerCase();
    const membershipNum = (p.membership_number || "").toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch = !searchTerm || 
      name.includes(searchLower) || 
      parentName.includes(searchLower) ||
      membershipNum.includes(searchLower);
    
    const matchesTeam = teamFilter === "all" || 
      (teamFilter === "unassigned" && !p.team_id) ||
      (teamFilter === "assigned" && p.team_id) ||
      p.team_id === teamFilter;
    
    const matchesTelegram = telegramFilter === "all" ||
      (telegramFilter === "yes" && p.parent_telegram) ||
      (telegramFilter === "no" && !p.parent_telegram);
    
    const matchesAgeGroup = ageGroupFilter === "all" || p.age_group === ageGroupFilter;
    
    return matchesSearch && matchesTeam && matchesTelegram && matchesAgeGroup;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Academy
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white">📋 Player Overview</h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-blue-500/80 text-white text-lg px-4 py-1">
              {filteredPlayers.length} of {players.length} players
            </Badge>
            <Button onClick={fetchData} variant="outline" size="sm" className="border-slate-600">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={handleReconcile} 
              variant="outline" 
              size="sm" 
              className="border-slate-600"
              disabled={reconciling}
              title="Sync player data between collections"
              data-testid="reconcile-players-btn"
            >
              {reconciling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Reconcile Data
            </Button>
            <Button onClick={exportToCSV} className="bg-emerald-600 hover:bg-emerald-700">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="container mx-auto px-4 py-4">
        <Card className="p-4 bg-slate-900/50 border-slate-700">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Filters:</span>
            </div>
            
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search player, parent, or membership #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-slate-800/50 border-slate-700 text-white"
              />
            </div>

            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-[160px] bg-slate-800/50 border-slate-700 text-white">
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {teams.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={ageGroupFilter} onValueChange={setAgeGroupFilter}>
              <SelectTrigger className="w-[120px] bg-slate-800/50 border-slate-700 text-white">
                <SelectValue placeholder="Age Group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ages</SelectItem>
                {ageGroups.map(ag => (
                  <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={telegramFilter} onValueChange={setTelegramFilter}>
              <SelectTrigger className="w-[140px] bg-slate-800/50 border-slate-700 text-white">
                <SelectValue placeholder="Telegram" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">On Telegram</SelectItem>
                <SelectItem value="no">Not on Telegram</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-8">
        <Card className="p-6 bg-slate-900/50 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-bold">All Academy Players</h2>
          </div>

          {filteredPlayers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-300">No Players Found</h3>
              <p className="text-slate-400 mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Player Name</TableHead>
                    <TableHead className="text-center">Age</TableHead>
                    <TableHead>Enrolled Sessions</TableHead>
                    <TableHead>Parent</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-center">Telegram</TableHead>
                    <TableHead>Assign to Team</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPlayers.map((player) => (
                    <TableRow key={player.id} className="hover:bg-slate-800/30">
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">
                            {player.child_name || player.name || `${player.given_name || ''} ${player.last_name || ''}`}
                          </p>
                          <p className="text-xs text-slate-400">{player.membership_number}</p>
                          {!!(player.also_known_as || []).length && (
                            <p className="text-xs text-slate-500" data-testid={`aka-${player.id}`}>
                              aka {player.also_known_as.join(", ")}
                            </p>
                          )}
                          <button
                            onClick={() => editAliases(player)}
                            className="text-[11px] text-blue-400 underline hover:text-blue-300"
                            data-testid={`edit-aka-${player.id}`}
                          >
                            {(player.also_known_as || []).length ? "edit names" : "add known-as name"}
                          </button>
                          <Badge className={`mt-1 ${player.gender === 'female' ? 'bg-pink-500/20 text-pink-300' : 'bg-blue-500/20 text-blue-300'}`}>
                            {player.gender === 'female' ? '♀' : '♂'} {calculateAge(player.birthdate)} yrs
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-2xl font-bold text-white">{calculateAge(player.birthdate)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 max-w-[200px]">
                          {player.enrolled_sessions?.length > 0 ? (
                            player.enrolled_sessions.map((session, idx) => (
                              <div key={idx} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                                <span className="font-bold">{dayNames[session.day_of_week] || session.day_of_week}</span>
                                <span className="text-slate-400 ml-1">{session.time}</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-slate-500 text-sm">No sessions</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{player.parent_name || "N/A"}</p>
                          <p className="text-xs text-slate-400">{player.parent_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-white">{player.parent_phone || "N/A"}</p>
                      </TableCell>
                      <TableCell className="text-center">
                        {player.parent_telegram ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400">
                            <Check className="w-3 h-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400">
                            <X className="w-3 h-3 mr-1" />
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {player.team_name && player.team_id && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 mb-1 block w-fit">
                              {player.team_name}
                            </Badge>
                          )}
                          <select
                            className="text-sm border rounded px-3 py-2 min-w-[150px] bg-slate-800 border-slate-600 text-white"
                            value={player.team_id || ""}
                            disabled={assigning === player.id}
                            onChange={(e) => handleAssignToTeam(player.id, e.target.value)}
                          >
                            <option value="">Select team...</option>
                            <option value="unassigned">— Unassign —</option>
                            {teams.map((team) => (
                              <option key={team.id} value={team.id}>
                                {team.name}
                              </option>
                            ))}
                          </select>
                          {assigning === player.id && (
                            <Loader2 className="w-4 h-4 animate-spin inline ml-2" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => openDeleteDialog(player)}
                          title="Delete Player"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Delete Player Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, player: null, checking: false, transactionInfo: null })}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Delete Player
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {deleteDialog.player && (
                <>Delete <strong className="text-white">{deleteDialog.player.child_name || deleteDialog.player.name}</strong>?</>
              )}
            </DialogDescription>
          </DialogHeader>

          {deleteDialog.checking ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-4" />
              <p className="text-slate-400">Checking for transactions...</p>
            </div>
          ) : deleteDialog.transactionInfo && (
            <div className="space-y-4">
              {/* Player Info */}
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-slate-400">Name</p>
                    <p className="text-white font-medium">{deleteDialog.transactionInfo.player_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Membership #</p>
                    <p className="text-white font-medium">{deleteDialog.transactionInfo.membership_number || "N/A"}</p>
                  </div>
                </div>
              </Card>

              {/* Transaction Check */}
              {deleteDialog.transactionInfo.has_transactions ? (
                <Card className="p-4 bg-red-500/10 border-red-500/30">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-400">Cannot Delete - Has Transactions</p>
                      <p className="text-sm text-slate-400 mt-1">This player has transaction history and cannot be deleted:</p>
                      <ul className="mt-2 text-sm space-y-1">
                        {deleteDialog.transactionInfo.transactions.map((t, i) => (
                          <li key={i} className="text-slate-300">• {t.description}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              ) : (
                <>
                  <Card className="p-4 bg-emerald-500/10 border-emerald-500/30">
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="font-medium text-emerald-400">Safe to Delete</p>
                        <p className="text-sm text-slate-400">No transactions found. This player can be deleted.</p>
                      </div>
                    </div>
                  </Card>

                  {/* Membership Number Options */}
                  <Card className="p-4 bg-slate-800/50 border-slate-700">
                    <p className="font-medium text-white mb-3">Membership Number: {deleteDialog.transactionInfo.membership_number}</p>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="lock_membership"
                          checked={lockMembershipNumber}
                          onCheckedChange={setLockMembershipNumber}
                        />
                        <Label htmlFor="lock_membership" className="text-slate-300 cursor-pointer">
                          Lock membership number (recommended for audit)
                        </Label>
                      </div>
                      <p className="text-xs text-slate-500 pl-6">
                        {lockMembershipNumber 
                          ? "The membership number will be reserved and cannot be reused."
                          : "The membership number can be reused for future registrations."}
                      </p>
                    </div>
                  </Card>
                </>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, player: null, checking: false, transactionInfo: null })}
              className="border-slate-600"
            >
              Cancel
            </Button>
            {deleteDialog.transactionInfo && !deleteDialog.transactionInfo.has_transactions && (
              <Button 
                onClick={handleDeletePlayer}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Player
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
